/**
 * The Summary Reports dialog: five stages behind one box — save, update, load, share and a
 * confirmation. This file is the shell. It owns mode transitions, entity construction, the
 * service calls and the busy/error state they share; each stage owns its own controls.
 *
 * It hands the View outcomes rather than events:
 *
 *   - onSelectReportForLoad(report)  a row was picked; the View holds it pending
 *   - onReportLoadConfirmed(report)  apply this report
 *   - onActiveReportChange(report)   saved or updated; this one is now active
 *   - onReportChanged(report)        a stored report was renamed or shared
 *   - onReportDeleted(id)            a stored report was deleted
 *   - onNotify(message)              say what just happened
 *
 * The middle two are separate because row actions reach reports that aren't loaded, and
 * renaming one you don't have open must not make it active. The View reconciles by id.
 *
 * `onNotify` is its own channel because the outcomes are deliberately coarse — a save and an
 * update both arrive as onActiveReportChange. Only in here is it known which the user did.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import type {
    SummaryReportChartState,
    SummaryReportDialogMode,
    SummaryReport,
    SummaryReportFilterState,
    SummaryReportGridState,
    SummaryReportPivotState,
} from '../../types.ts';
import { SummaryReportService } from '../../../../services/SummaryReportService.ts';
import {
    dialogCloseSx,
    dialogContentSx,
    dialogDismissButtonSx,
    dialogRailSx,
} from './dialogStyles.ts';
import ConfirmOverwrite from './stages/ConfirmOverwrite.tsx';
import ReportList from './stages/ReportList.tsx';
import SaveReportForm from './stages/SaveReportForm.tsx';
import ShareReport from './stages/ShareReport.tsx';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Service errors carry a usable message; anything else gets the caller's fallback. */
function errorText(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
}

const TITLES: Record<SummaryReportDialogMode, string> = {
    save: 'Save Summary Report',
    update: 'Update Summary Report',
    // Rows carry rename, share and delete, so this is more than a load surface.
    load: 'Your Summary Reports',
    share: 'Share Summary Report',
    confirmOverwrite: 'Replace Current View?',
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface SummaryReportDialogProps {
    open: boolean;
    mode: SummaryReportDialogMode;
    onClose: () => void;
    onModeChange: (mode: SummaryReportDialogMode) => void;

    /** Context the dialog reads from the View. */
    userId: string;
    definitionExpression: string;
    activeReport: SummaryReport | null;
    /**
     * The report awaiting load confirmation. View-owned because it arrives from two places:
     * a row in this list, or a ?reportId= link the View resolves.
     */
    pendingReport: SummaryReport | null;
    /**
     * Snapshots the grid for a save. Three pieces, because `exportState()` omits the pivot
     * and carries a stale chart while pivoted.
     */
    exportGridState: () => {
        gridState: SummaryReportGridState;
        pivotState?: SummaryReportPivotState;
        chartState?: SummaryReportChartState;
    };
    /** Snapshots the filter criteria behind `definitionExpression` for a save. */
    captureFilterState: () => SummaryReportFilterState;

    /** Outcomes handed back to the View. */
    onSelectReportForLoad: (report: SummaryReport) => void;
    onReportLoadConfirmed: (report: SummaryReport) => void;
    onActiveReportChange: (report: SummaryReport) => void;
    onReportChanged: (report: SummaryReport) => void;
    onReportDeleted: (id: string) => void;
    /** Confirms a completed action to the user. Called once per action, after it has landed. */
    onNotify: (message: string) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function SummaryReportsDialog({
    open,
    mode,
    onClose,
    onModeChange,
    userId,
    definitionExpression,
    activeReport,
    pendingReport,
    exportGridState,
    captureFilterState,
    onSelectReportForLoad,
    onReportLoadConfirmed,
    onActiveReportChange,
    onReportChanged,
    onReportDeleted,
    onNotify,
}: SummaryReportDialogProps) {
    // ── State ────────────────────────────────────────────────────────────────
    // Only what more than one stage needs, or what the action rail reads. Anything a single
    // stage owns alone lives in that stage.

    // The form fields are here rather than in the stage because the buttons that submit
    // them sit in the dialog's action rail, outside it.
    const [reportName, setReportName] = useState('');
    const [reportDescription, setReportDescription] = useState('');

    const [loadTab, setLoadTab] = useState(0);
    const [availableReports, setAvailableReports] = useState<SummaryReport[]>([]);

    // Share can target a row rather than the loaded report. Null means the active one.
    const [shareTargetReport, setShareTargetReport] = useState<SummaryReport | null>(null);
    // Share entered from a row returns to the list on Done rather than closing.
    const [shareReturnsToList, setShareReturnsToList] = useState(false);

    // Set by "Save Current First": the save that follows continues into the load the user
    // already asked for, rather than ending there.
    const [continueLoadAfterSave, setContinueLoadAfterSave] = useState(false);

    // isBusy disables the confirming actions so a slow write can't be submitted twice.
    const [isBusy, setIsBusy] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const shareTarget = shareTargetReport ?? activeReport;

    // ── Service plumbing ─────────────────────────────────────────────────────
    // The one path every write takes, and the one read that feeds the list.

    /**
     * Every write goes through here: one busy flag, one error surface, one place that knows
     * a failed write must not read as a successful one. Resolves false when it failed, so a
     * caller can leave its editor open.
     */
    const runWrite = useCallback(
        async (action: () => Promise<void>, failureMessage: string): Promise<boolean> => {
            setIsBusy(true);
            setErrorMessage(null);
            try {
                await action();
                return true;
            } catch (e) {
                setErrorMessage(errorText(e, failureMessage));
                return false;
            } finally {
                setIsBusy(false);
            }
        },
        []
    );

    // A slow fetch must not overwrite the results of one started after it.
    const newestRequestRef = useRef(0);
    const refreshReports = useCallback(async () => {
        const thisRequest = ++newestRequestRef.current;
        const isStillNewest = () => thisRequest === newestRequestRef.current;
        try {
            const reports = await SummaryReportService.getAllSummaryReportsForUser(userId);
            if (isStillNewest()) setAvailableReports(reports);
        } catch (e) {
            if (isStillNewest()) {
                setAvailableReports([]);
                setErrorMessage(errorText(e, 'Could not load your saved reports.'));
            }
        }
    }, [userId]);

    // ── Lifecycle ────────────────────────────────────────────────────────────
    // What the dialog does on opening, on changing stage, and on closing.

    useEffect(() => {
        if (open && mode === 'load') refreshReports();
    }, [open, mode, refreshReports]);

    // Prefill the form on entering update mode. Guarded by report id, so a re-render doesn't
    // clobber what the user has typed.
    const prefilledForRef = useRef<string | null>(null);
    useEffect(() => {
        if (!open || mode !== 'update') {
            prefilledForRef.current = null;
            return;
        }
        if (prefilledForRef.current === activeReport?.id) return;
        prefilledForRef.current = activeReport?.id ?? null;
        setReportName(activeReport?.name ?? '');
        setReportDescription(activeReport?.description ?? '');
    }, [open, mode, activeReport]);

    useEffect(() => {
        if (!open) {
            setReportName('');
            setReportDescription('');
            setLoadTab(0);
            setShareTargetReport(null);
            setShareReturnsToList(false);
            setContinueLoadAfterSave(false);
            setErrorMessage(null);
            prefilledForRef.current = null;
        }
    }, [open]);

    // ── Save and update ──────────────────────────────────────────────────────
    // Building the entity, writing it, and deciding which report ends up active.

    /**
     * Ends a save or update.
     *
     * On the "Save Current First" detour the user already named the report they wanted, so
     * the save continues into that load. Exactly one report ends up active: the pending one
     * when continuing, the freshly saved one otherwise.
     */
    const finishSave = useCallback(
        (saved: SummaryReport, verb: 'Saved' | 'Updated') => {
            if (continueLoadAfterSave && pendingReport) {
                // One notice covering both halves; a second would replace the first before
                // either was read.
                onNotify(`${verb} "${saved.name}" and loaded "${pendingReport.name}"`);
                onReportLoadConfirmed(pendingReport);
            } else {
                onNotify(`${verb} "${saved.name}"`);
                onActiveReportChange(saved);
            }
            onClose();
        },
        [
            continueLoadAfterSave,
            pendingReport,
            onReportLoadConfirmed,
            onActiveReportChange,
            onNotify,
            onClose,
        ]
    );

    // Create a new report.
    const handleSave = useCallback(async () => {
        if (!reportName.trim()) return;

        const { gridState, pivotState, chartState } = exportGridState();
        const report: SummaryReport = {
            id: crypto.randomUUID(),
            name: reportName.trim(),
            description: reportDescription.trim() || undefined,
            createdBy: userId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            visibility: 'private',
            sharedWithUserIds: [],
            sharedWithOrgIds: [],
            definitionExpression,
            filterState: captureFilterState(),
            gridState,
            pivotState,
            chartState,
        };
        await runWrite(async () => {
            // The stored report, not the one built above — the backend assigns the id.
            finishSave(await SummaryReportService.saveSummaryReport(report), 'Saved');
        }, 'Could not save this report.');
    }, [
        reportName,
        reportDescription,
        exportGridState,
        userId,
        definitionExpression,
        captureFilterState,
        finishSave,
        runWrite,
    ]);

    // Overwrite the loaded report in place.
    // Spreading activeReport keeps id, createdBy, createdAt and the sharing fields; only the
    // content and updatedAt are refreshed.
    const handleUpdate = useCallback(async () => {
        if (!activeReport || !reportName.trim()) return;

        // After the spread, so a report whose pivot or chart has since been cleared has the
        // old ones overwritten rather than inherited.
        const { gridState, pivotState, chartState } = exportGridState();
        const updated: SummaryReport = {
            ...activeReport,
            name: reportName.trim(),
            description: reportDescription.trim() || undefined,
            definitionExpression,
            filterState: captureFilterState(),
            gridState,
            pivotState,
            chartState,
            updatedAt: new Date().toISOString(),
        };
        await runWrite(async () => {
            finishSave(await SummaryReportService.updateSummaryReport(updated), 'Updated');
        }, 'Could not update this report.');
    }, [
        activeReport,
        reportName,
        reportDescription,
        definitionExpression,
        exportGridState,
        captureFilterState,
        finishSave,
        runWrite,
    ]);

    // ── Loading a report ─────────────────────────────────────────────────────
    // Picking one, confirming the overwrite, and the detour that saves first.

    // A row was picked, so confirm before replacing the view.
    const handleSelectReport = useCallback(
        (report: SummaryReport) => {
            onSelectReportForLoad(report);
            onModeChange('confirmOverwrite');
        },
        [onSelectReportForLoad, onModeChange]
    );

    // Confirmed: apply the pending report.
    const handleConfirmOverwrite = useCallback(() => {
        if (pendingReport) {
            onNotify(`Loaded "${pendingReport.name}"`);
            onReportLoadConfirmed(pendingReport);
        }
        onClose();
    }, [pendingReport, onReportLoadConfirmed, onNotify, onClose]);

    // Save Current First: update the loaded report, or create one if none is loaded.
    // The pending report is kept so the save can continue into it. See finishSave.
    const handleSaveCurrentFirst = useCallback(() => {
        setContinueLoadAfterSave(true);
        onModeChange(activeReport ? 'update' : 'save');
    }, [onModeChange, activeReport]);

    // ── Row actions ──────────────────────────────────────────────────────────
    // Reached from the list, so they act on reports that may not be the loaded one. Each
    // resolves false when the write failed, leaving the row's editor open.

    // Name and description both arrive already trimmed from the row.
    const commitRename = useCallback(
        (report: SummaryReport, name: string, description?: string) =>
            runWrite(async () => {
                const updated: SummaryReport = {
                    ...report,
                    name,
                    description,
                    updatedAt: new Date().toISOString(),
                };
                onReportChanged(await SummaryReportService.updateSummaryReport(updated));
                onNotify(`Renamed "${report.name}" to "${name}"`);
                await refreshReports();
            }, `Could not rename "${report.name}".`),
        [onReportChanged, onNotify, refreshReports, runWrite]
    );

    const confirmDelete = useCallback(
        (report: SummaryReport) =>
            runWrite(async () => {
                await SummaryReportService.deleteSummaryReport(report.id);
                onNotify(`Deleted "${report.name}"`);
                onReportDeleted(report.id);
                await refreshReports();
            }, `Could not delete "${report.name}".`),
        [onReportDeleted, onNotify, refreshReports, runWrite]
    );

    // ── Sharing ──────────────────────────────────────────────────────────────
    // Entered from a row or from the toolbar; the two differ only in where Done returns to.

    const startShare = useCallback(
        (report: SummaryReport) => {
            setShareTargetReport(report);
            setShareReturnsToList(true);
            onModeChange('share');
        },
        [onModeChange]
    );

    const handleShareDone = useCallback(() => {
        if (shareReturnsToList) {
            setShareTargetReport(null);
            setShareReturnsToList(false);
            onModeChange('load');
            return;
        }
        onClose();
    }, [shareReturnsToList, onModeChange, onClose]);

    /**
     * Always emits onReportChanged. The View promotes it to active only when the ids match,
     * so sharing a row you don't have loaded can't hijack the current view.
     */
    const handleShareWithUsers = useCallback(
        (userIds: string[]) => {
            if (!shareTarget) return Promise.resolve(false);
            const target = shareTarget;
            return runWrite(async () => {
                const updated: SummaryReport = {
                    ...target,
                    visibility: 'shared',
                    sharedWithUserIds: [...new Set([...target.sharedWithUserIds, ...userIds])],
                    updatedAt: new Date().toISOString(),
                };
                const stored = await SummaryReportService.updateSummaryReport(updated);
                // What they asked for, not what changed: re-sharing with someone who already
                // had it adds nobody, and "shared with 0 people" reads as a failure.
                onNotify(
                    `Shared "${target.name}" with ${userIds.length} ` +
                        `${userIds.length === 1 ? 'person' : 'people'}`
                );
                onReportChanged(stored);
                setShareTargetReport(stored);
                await refreshReports();
            }, `Could not share "${target.name}".`);
        },
        [shareTarget, onReportChanged, onNotify, refreshReports, runWrite]
    );

    // ── Render ───────────────────────────────────────────────────────────────

    const isForm = mode === 'save' || mode === 'update';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='sm'
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 2,
                        height: '62vh',
                        width: '62vw',
                        minWidth: 400,
                        minHeight: 400,
                        maxWidth: 900,
                        maxHeight: 900,
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    ...dialogRailSx,
                }}
            >
                {TITLES[mode]}
                <Tooltip title='Close'>
                    <IconButton
                        size='small'
                        aria-label='close dialog'
                        onClick={onClose}
                        sx={dialogCloseSx}
                    >
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
            </DialogTitle>

            <DialogContent dividers sx={dialogContentSx}>
                {isForm && (
                    <SaveReportForm
                        name={reportName}
                        description={reportDescription}
                        onNameChange={setReportName}
                        onDescriptionChange={setReportDescription}
                        replacingReportName={mode === 'update' ? activeReport?.name : undefined}
                    />
                )}

                {mode === 'load' && (
                    <ReportList
                        reports={availableReports}
                        userId={userId}
                        tab={loadTab}
                        onTabChange={setLoadTab}
                        onOpen={handleSelectReport}
                        onShare={startShare}
                        onCommitRename={commitRename}
                        onConfirmDelete={confirmDelete}
                    />
                )}

                {mode === 'share' && (
                    <ShareReport
                        report={shareTarget}
                        userId={userId}
                        isBusy={isBusy}
                        onShare={handleShareWithUsers}
                        onError={setErrorMessage}
                    />
                )}

                {mode === 'confirmOverwrite' && <ConfirmOverwrite pendingReport={pendingReport} />}

                {errorMessage && (
                    <Alert severity='error' sx={{ mt: 2 }}>
                        {errorMessage}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1, ...dialogRailSx }}>
                {isForm && (
                    <>
                        <Button variant='outlined' onClick={onClose} sx={dialogDismissButtonSx}>
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            onClick={mode === 'save' ? handleSave : handleUpdate}
                            disabled={!reportName.trim() || isBusy}
                        >
                            {mode === 'save'
                                ? continueLoadAfterSave
                                    ? 'Save and Continue'
                                    : 'Save Report'
                                : continueLoadAfterSave
                                  ? 'Update and Continue'
                                  : 'Update Report'}
                        </Button>
                    </>
                )}
                {mode === 'share' && (
                    <Button variant='outlined' onClick={handleShareDone} sx={dialogDismissButtonSx}>
                        Done
                    </Button>
                )}
                {mode === 'confirmOverwrite' && (
                    <>
                        <Button variant='outlined' onClick={onClose} sx={dialogDismissButtonSx}>
                            Cancel
                        </Button>
                        <Button
                            variant='outlined'
                            onClick={handleSaveCurrentFirst}
                            sx={dialogDismissButtonSx}
                        >
                            Save Current First
                        </Button>
                        <Button
                            variant='contained'
                            color='warning'
                            onClick={handleConfirmOverwrite}
                        >
                            Continue Without Saving
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
